/**
 * RoundEnd — the gentle end-of-round summary.
 *
 * Lives during phase === 'roundEnd'. All tiles are revealed; we recap the
 * player's total score, how many clues they nailed, and offer two choices:
 * Another board or Back to hub.
 *
 * Aesthetic: the blue board panel softens into a cream/lamp-amber summary
 * card, the score totals count up, and the per-category breakdown sits
 * beneath as a small grid.
 */
import { motion } from 'motion/react';

import { useCountUp } from '@/juice';

import { CATEGORY_LABELS } from '@/games/categories/boards';
import { formatDollars, type BoardTileState } from '@/games/categories/engine';

import { celebrateSpring } from './motion';
import { Affirmation } from './Affirmation';

import './RoundEnd.css';

export interface RoundEndProps {
  score: number;
  tiles: ReadonlyArray<BoardTileState>;
  mode: 'daily' | 'practice';
  onAnother: () => void;
  onBack: () => void;
}

export function RoundEnd({
  score,
  tiles,
  mode,
  onAnother,
  onBack,
}: RoundEndProps): React.JSX.Element {
  const totalAnswered = tiles.filter((t) => t.revealed).length;
  const totalCorrect = tiles.filter((t) => t.correct === true).length;

  // Per-category breakdown.
  const byCategory = new Map<string, { correct: number; total: number; dollars: number }>();
  for (const tile of tiles) {
    const slug = tile.category;
    const bucket = byCategory.get(slug) ?? { correct: 0, total: 0, dollars: 0 };
    bucket.total += 1;
    if (tile.correct) {
      bucket.correct += 1;
      bucket.dollars += tile.value;
    }
    byCategory.set(slug, bucket);
  }

  const scoreUp = useCountUp({ to: score, durationMs: 1400 });

  return (
    <section className="tb-end" aria-label="Round summary">
      <motion.div
        className="tb-end__card"
        initial={{ y: 32, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={celebrateSpring}
      >
        <Affirmation score={score} totalAnswered={totalAnswered} totalCorrect={totalCorrect} />

        <div className="tb-end__score-block">
          <span className="tb-end__score-label">Final score</span>
          <span className="tb-end__score-value">{formatDollars(scoreUp)}</span>
        </div>

        <div className="tb-end__cats" role="list">
          {Array.from(byCategory.entries()).map(([slug, stats], i) => (
            <motion.div
              key={slug}
              role="listitem"
              className="tb-end__cat"
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.3 + i * 0.07, duration: 0.4 }}
            >
              <span className="tb-end__cat-label">
                {CATEGORY_LABELS[slug as keyof typeof CATEGORY_LABELS]}
              </span>
              <span className="tb-end__cat-stats">
                <span className="tb-end__cat-count">
                  {stats.correct}/{stats.total}
                </span>
                <span className="tb-end__cat-dollars">{formatDollars(stats.dollars)}</span>
              </span>
            </motion.div>
          ))}
        </div>

        <div className="tb-end__actions">
          <button
            type="button"
            className="tb-end__back"
            onClick={onBack}
            data-testid="round-end-back"
          >
            Categories
          </button>
          <button
            type="button"
            className="tb-end__another"
            onClick={onAnother}
            data-testid="round-end-another"
          >
            {mode === 'daily' ? 'Practice another board' : 'Another board'}
          </button>
        </div>
      </motion.div>
    </section>
  );
}
